import Close from '@mui/icons-material/Close'
import { Alert, Box, Button, IconButton, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  appointmentSymptoms,
  bffCrmBookAppointment,
  bffCrmBookCustomAppointment,
  bffCrmPatientAppointments,
} from '@fireflyhealth/api'
import {
  AvailablePhysicianSlot,
  isServerVisitReason,
  VisitReason,
  type LegacyVisitReason,
  type ServerVisitReason,
} from '../AppointmentService'
import { useGetSymptomVisitType } from '../visitTypes'
import { useQuery } from '~/shared/hooks/query'
import { FFModal, Loader } from '~/shared/ui'
import { useSnackbar } from 'notistack'
import BookAppointment from './BookAppointment'
import BookingReason from './BookingReason'
import PrimaryConcern, { SymptomItem, Symptom, SymptomCategory } from './PrimaryConcern'
import CustomAppointmentBookingReason from './CustomAppointmentBookingReason'
import { MedicalEstablishedVisits } from '../utils'

type Step = 'book' | 'reason' | 'selectConcern' | 'customAppointmentReason'

// Partial: the server's reason set is wider than the legacy wizard's keys, and the triage
// flow's reason may be one this menu has no legacy equivalent for.
const Mapping: Partial<Record<ServerVisitReason, LegacyVisitReason>> = {
  Video: 'video',
  'Video-New': 'videoNew',
  'Health Guide Consult': 'healthGuideConsult',
  'Behavioral Health': 'behavioralHealth',
  Focused: 'focused',
}

type Props = {
  patientId: number
  personId: number
  onClose: () => void
  openModal?: boolean
  visitType: VisitReason | null
  visitTypeTitle: string
  // Whether the selected appointment type group routes through symptom triage.
  // Only meaningful when visitType is a server key; the legacy wizard infers
  // triage routing from the visit type instead.
  needsSymptomTriage?: boolean
  // The selected group's id, sent with the triage request so the resolver
  // prefers a type inside that group. Absent on the legacy wizard.
  appointmentTypeGroupId?: number
  // Optional initial values to prefill from an existing appointment during reschedule
  initialSymptomIds?: number[]
  initialOtherSymptoms?: string[]
  initialSymptomsSelected?: SymptomItem[]
}

const BookAppointmentModalWindow: FC<Props> = ({
  patientId,
  personId,
  onClose,
  openModal,
  visitType,
  visitTypeTitle,
  needsSymptomTriage,
  appointmentTypeGroupId,
  initialSymptomIds,
  initialOtherSymptoms,
  initialSymptomsSelected,
}) => {
  let modalTitle: string = ''
  const now = new Date()
  const [visitReason, setVisitReason] = useState<VisitReason | null>(visitType)
  // Triage can resolve a type outside the clicked group, so the header follows
  // the resolved group's title rather than the seeded one.
  const [resolvedVisitTypeTitle, setResolvedVisitTypeTitle] = useState(visitTypeTitle)
  const [allSymptoms, setAllSymptoms] = useState<Symptom[]>([])
  const [categories, setCategories] = useState<SymptomCategory[]>([])
  const [step, setStep] = useState<Step>(
    (
      visitType != null && isServerVisitReason(visitType)
        ? needsSymptomTriage === true
        : visitType == 'video'
    )
      ? 'selectConcern'
      : 'book',
  )
  const [date, setDate] = useState(now)
  const [slot, setSlot] = useState<AvailablePhysicianSlot | null>(null)
  const [isCustomAppointment, setIsCustomAppointment] = useState(false)
  const [selectedCustomReasons, setSelectedCustomReasons] = useState<string[]>([])
  const [customAppointmentReasons, setCustomAppointmentReasons] = useState<string[]>([])
  const [otherReason, setOtherReason] = useState<string>('')

  const [symptomsSelected, setSymptomsSelected] = useState<SymptomItem[]>([])
  const [otherSymptomsSelected, setOtherSymptomsSelected] = useState<string[]>([])
  const [apptReason, setApptReason] = useState<string>('')
  const [saving, setSaving] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Whether this booking runs through symptom triage. The group carries the
  // answer on the server-driven path; the legacy path infers it from the visit
  // type. A triage booking collects concerns, so its description is optional
  // and its slot step offers a way back to the concerns step.
  const isTriageFlow =
    visitReason != null &&
    (isServerVisitReason(visitReason)
      ? needsSymptomTriage === true
      : MedicalEstablishedVisits.includes(visitReason))

  // Triage has something to resolve only when a triage booking holds concerns.
  // A concernless triage booking (a legacy type that opens straight on the book
  // step) books the seeded type directly.
  const triageEnabled =
    isTriageFlow && (symptomsSelected.length > 0 || otherSymptomsSelected.length > 0)

  // Prefetches while concerns are still being picked, so the book step opens
  // already resolved instead of flashing the seeded type and title.
  const { result: symptomVisitType, isError: triageErrored } = useGetSymptomVisitType({
    symptom_ids: symptomsSelected.flatMap(({ id }) => (id !== undefined ? [id] : [])),
    other_symptoms: otherSymptomsSelected.length ? otherSymptomsSelected.join(', ') : undefined,
    patient_id: patientId,
    selected_appointment_type_group_id: appointmentTypeGroupId,
    enabled: triageEnabled,
  })

  // Triage resolves the visit type from the selected symptoms. The server-driven
  // wizard takes the returned unique_key as-is and retitles the wizard from the
  // resolved type's group, since triage can land outside the clicked group. The
  // legacy wizard maps the display name back to its camelCase key first.
  if (symptomVisitType && visitReason && isTriageFlow) {
    const triagedReason = isServerVisitReason(visitReason)
      ? symptomVisitType.reason
      : Mapping[symptomVisitType.reason]
    if (triagedReason && triagedReason !== visitReason) setVisitReason(triagedReason)
    const triagedTitle = symptomVisitType.appointment_type.group.title
    if (isServerVisitReason(visitReason) && triagedTitle !== resolvedVisitTypeTitle) {
      setResolvedVisitTypeTitle(triagedTitle)
    }
  }

  // The book step operates on the triage result, so it (and its header) waits
  // until state matches the current response — the server wizard by unique_key,
  // the legacy wizard by mapped key, where an unmappable reason keeps the seed.
  // The prefetch normally settles this before concerns are confirmed. A failed
  // read unblocks the step with the seeded type and a warning. Failing open is
  // safe here — unlike the app, CRM never acts on the response's red flags, so
  // triage only refines the type choice.
  const triageResolved =
    !triageEnabled ||
    triageErrored ||
    visitReason == null ||
    (isServerVisitReason(visitReason)
      ? symptomVisitType?.reason === visitReason
      : symptomVisitType != null &&
        (Mapping[symptomVisitType.reason] === undefined ||
          Mapping[symptomVisitType.reason] === visitReason))

  const { data: symptomData } = useQuery(
    appointmentSymptoms.retrieveQueryKey(),
    async () => {
      const response = await appointmentSymptoms.retrieve()
      return response.data
    },
    {},
    { error: 'Failed to fetch symptoms' },
  )
  const { enqueueSnackbar } = useSnackbar()
  const client = useQueryClient()

  // Each catalog response replaces the symptoms and categories the concern pickers show.
  const [loadedSymptomData, setLoadedSymptomData] = useState<typeof symptomData>()
  if (symptomData && symptomData !== loadedSymptomData) {
    setLoadedSymptomData(symptomData)
    setIsLoading(false)
    setAllSymptoms(symptomData.all_symptoms)
    setCategories(symptomData.home_page_categories)
  }

  // A reschedule prefills the concerns whenever the selection is empty: full symptoms at once,
  // or bare ids once the catalog arrives to label them.
  if (symptomsSelected.length === 0 && initialSymptomsSelected?.length) {
    setSymptomsSelected(initialSymptomsSelected)
  }
  if (otherSymptomsSelected.length === 0 && initialOtherSymptoms?.length) {
    setOtherSymptomsSelected(initialOtherSymptoms)
  }
  if (symptomData && symptomsSelected.length === 0 && initialSymptomIds?.length) {
    const idSet = new Set(initialSymptomIds)
    const mapped = (symptomData.all_symptoms || [])
      .filter(s => idSet.has(s.id))
      .map(s => ({ id: s.id, label: s.label }))
    if (mapped.length) setSymptomsSelected(mapped)
  }

  const handleBookAppointment = async () => {
    if (apptReason === '' && visitReason && !isTriageFlow) {
      return
    }
    // A slot (with a scheduled time) is always picked before the Book step is
    // reachable; this guard also narrows the generated body's required fields.
    if (!slot?.scheduled_date) {
      return
    }
    setSaving(true)
    const body = {
      description: apptReason,
      reason: slot.reason,
      patient_id: patientId,
      symptom_ids: symptomsSelected.map(({ id }) => id).filter((id): id is number => id != null),
      other_symptoms: otherSymptomsSelected.length ? otherSymptomsSelected.join(', ') : null,
      scheduled_date: slot.scheduled_date,
      physician_id: slot.physician_id,
    }

    try {
      const { data } = await bffCrmBookAppointment.create({ data: body })
      if (data.appointment?.patient != null) {
        enqueueSnackbar(`Appointment booked successfully`, { variant: 'success' })
      } else {
        enqueueSnackbar(`Failed to book the appointment`, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(`Failed to book the appointment`, { variant: 'error' })
    }
    await client.invalidateQueries({
      queryKey: bffCrmPatientAppointments.retrieveQueryKey({ patient_id: patientId }),
    })
    await client.invalidateQueries({ queryKey: ['useAppointmentLogs', patientId] })
    setSaving(false)
    onClose()
  }

  const handleCustomBookAppointment = async () => {
    if (apptReason === '' && selectedCustomReasons.length === 0 && visitReason && !isTriageFlow) {
      return
    }
    // A slot (with a scheduled time) is always picked before the Book step is
    // reachable; this guard also narrows the generated body's required fields.
    if (!slot?.scheduled_date) {
      return
    }
    setSaving(true)
    const customAppointmentReasons = [...selectedCustomReasons]
    if (customAppointmentReasons.includes('Other')) {
      customAppointmentReasons[customAppointmentReasons.indexOf('Other')] = otherReason
    }
    const body = {
      description: apptReason,
      reason: slot.reason,
      patient_id: patientId,
      symptom_ids: symptomsSelected.map(({ id }) => id).filter((id): id is number => id != null),
      other_symptoms: otherSymptomsSelected.length ? otherSymptomsSelected.join(', ') : null,
      scheduled_date: slot.scheduled_date,
      physician_id: slot.physician_id,
      custom_appointment_reasons: customAppointmentReasons,
    }

    try {
      const { data } = await bffCrmBookCustomAppointment.create({ data: body })
      if (data.appointment?.patient != null) {
        enqueueSnackbar(`Appointment booked successfully`, { variant: 'success' })
      } else {
        enqueueSnackbar(`Failed to book the appointment`, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(`Failed to book the appointment`, { variant: 'error' })
    }
    await client.invalidateQueries({
      queryKey: bffCrmPatientAppointments.retrieveQueryKey({ patient_id: patientId }),
    })
    await client.invalidateQueries({ queryKey: ['useAppointmentLogs', patientId] })
    setSaving(false)
    onClose()
  }

  const stepBook = () => {
    return (
      <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
        {isTriageFlow ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'inline-table',
            }}
          >
            <Button
              onClick={() => {
                setStep('selectConcern')
              }}
              variant="text"
              disabled={saving}
              size="small"
              sx={{
                borderRadius: '40px',
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '20%',
                marginRight: 1,
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Back'}</Typography>
            </Button>
          </Box>
        ) : null}
        <Button
          onClick={() => {
            onClose()
          }}
          variant="text"
          // disabled={saving}
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
            maxHeight: '75%',
            width: '20%',
            marginRight: 1,
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Cancel'}</Typography>
        </Button>
        <Button
          disabled={slot ? false : true}
          onClick={() => {
            setStep('reason')
          }}
          variant="contained"
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Next'}</Typography>
        </Button>
      </Box>
    )
  }

  const stepReason = () => {
    return (
      <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'inline-table',
          }}
        >
          <Button
            onClick={() => {
              setStep('book')
            }}
            variant="text"
            disabled={saving}
            size="small"
            sx={{
              borderRadius: '40px',
              fontSize: '1.4rem',
              maxHeight: '75%',
              width: '20%',
              marginRight: 1,
            }}
          >
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Back'}</Typography>
          </Button>
        </Box>
        <Button
          onClick={() => {
            onClose()
          }}
          variant="text"
          disabled={saving}
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
            maxHeight: '75%',
            width: '20%',
            marginRight: 1,
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Cancel'}</Typography>
        </Button>
        {isCustomAppointment && (
          <Button
            disabled={saving || (apptReason === '' && visitReason !== null && !isTriageFlow)}
            onClick={() => {
              setStep('customAppointmentReason')
            }}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '40px',
              fontSize: '1.4rem',
            }}
          >
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Next'}</Typography>
          </Button>
        )}
        {!isCustomAppointment && (
          <Button
            disabled={saving || (apptReason === '' && visitReason !== null && !isTriageFlow)}
            onClick={() => void handleBookAppointment()}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '40px',
              fontSize: '1.4rem',
            }}
          >
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Book'}</Typography>
          </Button>
        )}
      </Box>
    )
  }

  const stepCustomAppointmentReason = () => {
    let disabled = false
    if (selectedCustomReasons.includes('Other') && otherReason == '') {
      disabled = true
    }
    return (
      <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'inline-table',
          }}
        >
          <Button
            onClick={() => {
              setStep('reason')
            }}
            variant="text"
            disabled={saving}
            size="small"
            sx={{
              borderRadius: '40px',
              fontSize: '1.4rem',
              maxHeight: '75%',
              width: '20%',
              marginRight: 1,
            }}
          >
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Back'}</Typography>
          </Button>
        </Box>
        <Button
          onClick={() => {
            onClose()
          }}
          variant="text"
          disabled={saving}
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
            maxHeight: '75%',
            width: '20%',
            marginRight: 1,
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Cancel'}</Typography>
        </Button>
        <Button
          disabled={disabled || selectedCustomReasons.length === 0 || saving}
          onClick={() => void handleCustomBookAppointment()}
          variant="contained"
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Book'}</Typography>
        </Button>
      </Box>
    )
  }

  const stepSelectConcern = () => {
    return (
      <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            onClose()
          }}
          variant="text"
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
            maxHeight: '75%',
            width: '20%',
            marginRight: 1,
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Cancel'}</Typography>
        </Button>
        <Button
          disabled={symptomsSelected.length || otherSymptomsSelected.length ? false : true}
          onClick={() => {
            setStep('book')
          }}
          variant="contained"
          size="small"
          sx={{
            borderRadius: '40px',
            fontSize: '1.4rem',
          }}
        >
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Next'}</Typography>
        </Button>
      </Box>
    )
  }

  switch (step) {
    case 'book': {
      modalTitle = triageResolved ? 'Book ' + resolvedVisitTypeTitle : 'Book Appointment'
      break
    }
    case 'selectConcern': {
      modalTitle = 'Add Primary Concerns'
      break
    }
    case 'reason': {
      modalTitle = 'Reason for Visit'
      break
    }
    case 'customAppointmentReason': {
      modalTitle = 'Why was a custom appointment created?'
      break
    }
    default: {
      modalTitle = 'Book Appointment'
      break
    }
  }

  return (
    <FFModal
      open={openModal == true || openModal == null}
      style={{ width: '70%', marginLeft: 'auto', marginRight: 'auto' }}
      footer={
        visitReason ? (
          step == 'book' ? (
            stepBook()
          ) : step == 'selectConcern' ? (
            stepSelectConcern()
          ) : step == 'customAppointmentReason' ? (
            stepCustomAppointmentReason()
          ) : (
            stepReason()
          )
        ) : (
          <></>
        )
      }
    >
      <>
        {isLoading && <Loader />}
        <Box sx={{ m: 2 }}>
          <Typography variant="h6">{modalTitle}</Typography>
        </Box>
        <Box sx={{ m: 2 }}>
          <IconButton
            onClick={() => {
              onClose()
            }}
            sx={{
              position: 'absolute',
              zIndex: 100,
              top: 2,
              right: 2,
            }}
          >
            <Close />
          </IconButton>
        </Box>
        {step === 'book' ? (
          triageResolved ? (
            <>
              {isTriageFlow && triageErrored ? (
                <Alert severity="warning" sx={{ mx: 2 }}>
                  {"Couldn't confirm the visit type from the concerns — booking the selected type."}
                </Alert>
              ) : null}
              <BookAppointment
                patientId={patientId}
                personId={personId}
                visitReason={visitReason}
                now={now}
                date={date}
                setDate={setDate}
                slot={slot}
                setSlot={setSlot}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                setIsCustomAppointment={setIsCustomAppointment}
                setCustomAppointmentReasons={setCustomAppointmentReasons}
              />
            </>
          ) : (
            <Loader />
          )
        ) : step === 'selectConcern' ? (
          <PrimaryConcern
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            symptomsSelected={symptomsSelected}
            setSymptomsSelected={setSymptomsSelected}
            otherSymptomsSelected={otherSymptomsSelected}
            setOtherSymptomsSelected={setOtherSymptomsSelected}
            setVisitReason={setVisitReason}
            patientId={patientId}
            categories={categories}
            allSymptoms={allSymptoms}
            setAllSymptoms={setAllSymptoms}
          />
        ) : step === 'customAppointmentReason' ? (
          <CustomAppointmentBookingReason
            setCustomAppointmentReasons={setCustomAppointmentReasons}
            customAppointmentReasons={customAppointmentReasons}
            setSelectedCustomReasons={setSelectedCustomReasons}
            selectedCustomReasons={selectedCustomReasons}
            otherReason={otherReason}
            setOtherReason={setOtherReason}
            physicianId={slot?.physician_id}
            patientId={patientId}
            reason={slot?.reason}
            appointmentDateTime={slot?.scheduled_date}
          />
        ) : (
          <BookingReason
            apptReason={apptReason}
            setApptReason={setApptReason}
            visitReason={visitReason}
            isTriageFlow={isTriageFlow}
            saving={saving}
            symptomsSelected={symptomsSelected}
            otherSymptomsSelected={otherSymptomsSelected}
          />
        )}
      </>
    </FFModal>
  )
}

export default BookAppointmentModalWindow
